<template>
  <div class="ma-4">
    <v-alert v-if="isLoading" type="info">Looking up the necessary information...</v-alert>
    <v-alert v-else-if="!isOpen" type="error">{{ notOpenText }}</v-alert>
    <v-card v-else>
      <v-card-title>Capstone Course Evaluation</v-card-title>
      <v-card-text>
        <p>Please click on each capstone course displayed below to submit your feedback on that course.</p>
        <v-expansion-panels>
          <capstone-eval v-for="{ crn, course, courseTitle } in courses" :key="course" :crn="crn" :course="course" :course-title="courseTitle" :term="term" />
        </v-expansion-panels>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { computed, onMounted, reactive, ref } from '@vue/composition-api'
import { stringFormatDate } from '@/helpers/formatters'
import { courseList } from '@/components/student/capstone/courses'

export default {
  components: {
    CapstoneEval: () => import('@/components/student/capstone/eval')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const term = computed({
      get: () => root.$store.state.student.capstone.term,
      set: (term) => root.$store.commit('student/setCapstoneVal', { term })
    })
    const courses = computed({
      get: () => root.$store.state.student.capstone.courses,
      set: (courses) => root.$store.commit('student/setCapstoneVal', { courses })
    })

    const initFormData = reactive({})

    const isLoading = ref(true)
    const isOpen = ref(false)
    const notOpenText = ref('Capstone Course Evaluations are not yet available. Please return later to complete your evaluation.')
    const openDate = ref('')

    onMounted(async () => {
      if (user.value.roles.includes('Employee')) {
        root.$router.replace('/student/capstone/admin')
        return
      }
      // Get the current term
      const { data: termData } = await root.$feathers.service('system/term').find({ query: { type: 'Traditional', start: { $lte: new Date() }, $sort: { start: -1 }, $limit: 1 } })
      if (termData.length === 0) {
        isOpen.value = false
        return
      }
      if ('capstoneCourseEvals' in termData[0]) {
        const open = new Date(termData[0].capstoneCourseEvals.open)
        const close = new Date(termData[0].capstoneCourseEvals.close)
        const now = new Date()
        if (open < now && close > now) isOpen.value = true
        else if (close < now) {
          notOpenText.value = 'Capstone Course Evaluations for the most recent term have closed.'
          return
        } else if (open > now) {
          notOpenText.value = 'Capstone Course Evaluations are not yet available. Please return between ' + stringFormatDate(open, true, true) + ' and ' + stringFormatDate(close, true, true) + ' to complete your evaluation.'
          openDate.value = open
          return
        }
      }
      term.value = termData[0].term
      const aggregate = [
        { $match: { pidm: user.value.pidm, term: term.value } },
        { $unwind: '$schedule' },
        { $project: {
          term: 1,
          major: '$academics.major',
          crn: '$schedule.crn',
          course: {
            $substr: ['$schedule.title', 0, 6]
          },
          courseTitle: '$schedule.title'
        } },
        { $match: {
          course: {
            $in: courseList
          }
        } },
        { $sort: { term: -1 } }
      ]
      const { data: courseData } = await root.$feathers.service('student/term-detail').find({ query: { aggregate } })
      courses.value = courseData
      if (courses.value.length === 0) {
        root.$store.dispatch('main/snackbar', { color: 'error', text: 'You are not currently enrolled in any capstone courses for the current term, so there are no courses to load for evaluation.' })
        root.$router.replace('/student/capstone')
      }
      isLoading.value = false
    })

    return {
      term,
      courses,
      initFormData,
      isLoading,
      isOpen,
      notOpenText,
      openDate
    }
  }
}
</script>
